import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Truck, PackageCheck, Upload, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";
import { DeliveryBadge, StatusBadge, Countdown, fmtDate, inr } from "@/components/badges";

const CSV_TEMPLATE = "sku,name,category,brand,price,stock,moq,unit,express_eligible\nPLY-EX-01,Sample Plywood 18mm,plywood,CenturyPly,2400,50,2,sheet,true";

export default function VendorDashboard() {
  const { user } = useStore();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const [csv, setCsv] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [np, setNp] = useState({ sku: "", name: "", category: "plywood", brand: "", price: "", stock: "", moq: 1, unit: "sheet", express_eligible: false });
  const [edits, setEdits] = useState({});

  const vendor = user?.vendor;

  const loadOrders = () => api.get("/vendor/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadProducts = () => api.get("/vendor/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadStats = () => api.get("/vendor/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    loadOrders(); loadProducts(); loadStats();
    const t = setInterval(loadOrders, 10000);
    return () => clearInterval(t);
  }, []);

  const act = async (oid, action) => {
    try {
      await api.post(`/vendor/orders/${oid}/action`, { action });
      toast.success(`Order ${action}ed`);
      loadOrders(); loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async (pid) => {
    const e = edits[pid] || {};
    const patch = {};
    if (e.price !== undefined && e.price !== "") patch.price = parseFloat(e.price);
    if (e.stock !== undefined && e.stock !== "") patch.stock = parseInt(e.stock, 10);
    if (e.express_eligible !== undefined) patch.express_eligible = e.express_eligible;
    try {
      await api.patch(`/vendor/products/${pid}`, patch);
      toast.success("Saved");
      setEdits((prev) => ({ ...prev, [pid]: {} }));
      loadProducts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/vendor/products/${p.id}`, { active: !p.active });
      loadProducts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vendor/products", { ...np, price: parseFloat(np.price), stock: parseInt(np.stock, 10), moq: parseInt(np.moq, 10) });
      toast.success("Product added");
      setShowAdd(false);
      setNp({ sku: "", name: "", category: "plywood", brand: "", price: "", stock: "", moq: 1, unit: "sheet", express_eligible: false });
      loadProducts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runImport = async () => {
    try {
      const { data } = await api.post("/vendor/import", { csv_text: csv });
      setImportResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      loadProducts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const TABS = [["orders", "Orders"], ["catalog", "Catalog"], ["import", "Bulk import"], ["stats", "Performance"]];
  const pending = orders.filter((o) => o.status === "placed");
  const active = orders.filter((o) => ["accepted", "dispatched"].includes(o.status));
  const done = orders.filter((o) => ["delivered", "cancelled"].includes(o.status));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vendor-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-label">Vendor Portal</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">{vendor?.shop_name}</h1>
        </div>
        {stats && (
          <div className="flex gap-3 text-center">
            {[["Today", stats.orders_today], ["Pending", stats.pending_orders], ["Payout due", inr(stats.payout_due)]].map(([l, v]) => (
              <div key={l} className="card-surface px-4 py-2"><p className="font-display font-bold text-lg">{v}</p><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p></div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-200" data-testid="vendor-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`vendor-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-orange-700 text-orange-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {l}{k === "orders" && pending.length > 0 && <span className="ml-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{pending.length}</span>}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-6 space-y-6">
          {[["New — accept within SLA", pending, true], ["In progress", active, false], ["Completed", done, false]].map(([title, list, showSla]) => (
            <div key={title}>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">{title} ({list.length})</h2>
              <div className="mt-3 space-y-3">
                {list.length === 0 && <p className="text-sm text-slate-400">No orders.</p>}
                {list.map((o) => (
                  <div key={o.id} data-testid={`vendor-order-${o.order_no}`} className={`card-surface p-4 ${o.sla_breach || o.dispatch_breach ? "!border-red-400" : ""}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono2 text-sm font-bold">{o.order_no}</span>
                      <StatusBadge status={o.status} />
                      <DeliveryBadge mode={o.mode} />
                      <span className="text-xs text-slate-500">{fmtDate(o.created_at)} · {o.buyer_company || o.buyer_name}</span>
                      <span className="ml-auto font-display font-bold">{inr(o.subtotal)}</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-600">{o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}</p>
                    <p className="mt-1 text-xs text-slate-500">Deliver to: {o.address} · Substitutions {o.substitutions_allowed ? "allowed" : "off"}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {o.status === "placed" && (
                        <>
                          {showSla && <Countdown deadline={o.accept_deadline} label="to accept" />}
                          <button data-testid={`accept-${o.order_no}`} onClick={() => act(o.id, "accept")} className="btn-dark !px-3 !py-2 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Accept</button>
                          <button data-testid={`reject-${o.order_no}`} onClick={() => act(o.id, "reject")} className="btn-ghost-b2b !px-3 !py-2 text-xs !text-red-600 !border-red-300"><XCircle className="h-3.5 w-3.5" /> Reject</button>
                        </>
                      )}
                      {o.status === "accepted" && (
                        <>
                          {o.dispatch_deadline && <Countdown deadline={o.dispatch_deadline} label="to dispatch" />}
                          <button data-testid={`dispatch-${o.order_no}`} onClick={() => act(o.id, "dispatch")} className="btn-brand !px-3 !py-2 text-xs"><Truck className="h-3.5 w-3.5" /> Mark dispatched</button>
                        </>
                      )}
                      {o.status === "dispatched" && (
                        <button data-testid={`deliver-${o.order_no}`} onClick={() => act(o.id, "deliver")} className="btn-dark !px-3 !py-2 text-xs"><PackageCheck className="h-3.5 w-3.5" /> Mark delivered</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "catalog" && (
        <div className="mt-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{products.length} SKUs listed</p>
            <button data-testid="add-product-open" onClick={() => setShowAdd(!showAdd)} className="btn-brand !py-2 text-xs"><Plus className="h-3.5 w-3.5" /> Add product</button>
          </div>
          {showAdd && (
            <form onSubmit={addProduct} className="card-surface mt-4 p-5 grid sm:grid-cols-3 gap-3" data-testid="add-product-form">
              <input required placeholder="SKU" value={np.sku} onChange={(e) => setNp({ ...np, sku: e.target.value })} className="input-b2b" data-testid="np-sku" />
              <input required placeholder="Product name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="input-b2b sm:col-span-2" data-testid="np-name" />
              <select value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} className="input-b2b" data-testid="np-category">
                {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input placeholder="Brand" value={np.brand} onChange={(e) => setNp({ ...np, brand: e.target.value })} className="input-b2b" data-testid="np-brand" />
              <input required type="number" step="0.01" placeholder="Price (₹)" value={np.price} onChange={(e) => setNp({ ...np, price: e.target.value })} className="input-b2b" data-testid="np-price" />
              <input required type="number" placeholder="Stock" value={np.stock} onChange={(e) => setNp({ ...np, stock: e.target.value })} className="input-b2b" data-testid="np-stock" />
              <input type="number" placeholder="MOQ" value={np.moq} onChange={(e) => setNp({ ...np, moq: e.target.value })} className="input-b2b" data-testid="np-moq" />
              <input placeholder="Unit (sheet/pack…)" value={np.unit} onChange={(e) => setNp({ ...np, unit: e.target.value })} className="input-b2b" data-testid="np-unit" />
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={np.express_eligible} onChange={(e) => setNp({ ...np, express_eligible: e.target.checked })} className="h-4 w-4 accent-amber-500" data-testid="np-express" />
                Express-eligible
              </label>
              <button type="submit" className="btn-brand sm:col-span-3" data-testid="np-submit">Save product</button>
            </form>
          )}
          <div className="mt-4 card-surface overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]" data-testid="catalog-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="p-3">SKU / Name</th><th>Price (₹)</th><th>Stock</th><th>Express</th><th>Active</th><th></th>
              </tr></thead>
              <tbody>
                {products.map((p) => {
                  const e = edits[p.id] || {};
                  return (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`catalog-row-${p.sku}`}>
                      <td className="p-3"><p className="font-mono2 text-[11px] text-slate-400">{p.sku}</p><p className="font-semibold text-slate-900">{p.name}</p><p className="text-[11px] text-slate-500 capitalize">{p.category} · {p.brand}</p></td>
                      <td><input type="number" step="0.01" defaultValue={p.price} data-testid={`price-${p.sku}`}
                        onChange={(ev) => setEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], price: ev.target.value } }))}
                        className="input-b2b !w-24 !py-1.5" /></td>
                      <td><input type="number" defaultValue={p.stock} data-testid={`stock-${p.sku}`}
                        onChange={(ev) => setEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], stock: ev.target.value } }))}
                        className="input-b2b !w-20 !py-1.5" /></td>
                      <td><input type="checkbox" defaultChecked={p.express_eligible} data-testid={`express-${p.sku}`}
                        onChange={(ev) => setEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], express_eligible: ev.target.checked } }))}
                        className="h-4 w-4 accent-amber-500" /></td>
                      <td><button onClick={() => toggleActive(p)} data-testid={`active-${p.sku}`}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${p.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700" : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                        {p.active ? "Live" : "Hidden"}
                      </button></td>
                      <td><button onClick={() => saveEdit(p.id)} data-testid={`save-${p.sku}`} className="btn-dark !px-3 !py-1.5 text-xs">Save</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "import" && (
        <div className="mt-6 max-w-3xl" data-testid="import-panel">
          <p className="text-sm text-slate-600">Paste CSV rows. Columns: <code className="font-mono2 text-xs bg-slate-100 px-1 rounded">sku,name,category,brand,price,stock,moq,unit,express_eligible</code>. Existing SKUs are updated (upsert).</p>
          <textarea rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={CSV_TEMPLATE}
            className="input-b2b mt-3 font-mono2 text-xs" data-testid="csv-input" />
          <button onClick={runImport} disabled={!csv.trim()} className="btn-brand mt-3" data-testid="csv-import-btn"><Upload className="h-4 w-4" /> Import catalog</button>
          {importResult && (
            <div className="mt-4 card-surface p-4 text-sm" data-testid="import-result">
              <p className="font-semibold text-emerald-700">{importResult.created} created · {importResult.updated} updated</p>
              {importResult.errors.map((e, i) => <p key={i} className="text-red-600 text-xs mt-1">{e}</p>)}
            </div>
          )}
        </div>
      )}

      {tab === "stats" && stats && (
        <div className="mt-6 space-y-6" data-testid="vendor-stats">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Total orders", stats.total_orders], ["Acceptance rate", stats.acceptance_rate == null ? "—" : `${stats.acceptance_rate}%`],
              ["Revenue (delivered)", inr(stats.revenue)], ["Commission", inr(stats.commission)]].map(([l, v]) => (
              <div key={l} className="card-surface p-5"><p className="font-display text-2xl font-bold text-slate-900">{v}</p><p className="text-xs uppercase tracking-wider text-slate-500 mt-1">{l}</p></div>
            ))}
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payouts (delivered orders, {stats.vendor?.commission_pct}% commission)</p>
            {stats.payouts.length === 0 ? <p className="mt-3 text-sm text-slate-400">No delivered orders yet.</p> : (
              <table className="mt-3 w-full text-sm" data-testid="payout-table">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400"><th className="py-2">Order</th><th>Delivered</th><th className="text-right">Subtotal</th><th className="text-right">Commission</th><th className="text-right">Payout</th></tr></thead>
                <tbody>
                  {stats.payouts.map((p) => (
                    <tr key={p.order_no} className="border-t border-slate-100">
                      <td className="py-2 font-mono2 font-semibold">{p.order_no}</td>
                      <td className="text-xs text-slate-500">{fmtDate(p.delivered_at)}</td>
                      <td className="text-right">{inr(p.subtotal)}</td>
                      <td className="text-right text-red-600">-{inr(p.commission)}</td>
                      <td className="text-right font-bold text-emerald-700">{inr(p.payout)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
